import * as React from 'react';
import { DropdownPopover, Outside } from '@cian/ui-kit';

import { IconFunctionalSuccess16 } from '@cian/ui-kit-design-tokens/icons';

import * as styles from './FiltersDropdownPopover.css';
import { useFocusedItem } from './hooks';
import { IDialogFilterItem } from '../../../../types/filterSelectors';

interface IFiltersDropdownPopoverProps {
  anchor: React.ReactElement<any>;
  chipRef: React.RefObject<HTMLLIElement | null>;
  filters: IDialogFilterItem[];
  open: boolean;
  onClose: () => void;
  onFilter: (item: IDialogFilterItem) => void;
}

export const FiltersDropdownPopover: React.FC<IFiltersDropdownPopoverProps> = ({
  anchor,
  chipRef,
  filters,
  open,
  onClose,
  onFilter,
}) => {
  const insideRefs = React.useMemo(() => [chipRef], [chipRef]);

  const { focusedIndex, setFocusedIndex, focusedItemRef } = useFocusedItem({ isPopoverOpen: open });

  const focusChip = React.useCallback((element: HTMLElement) => {
    const tabbable = element.querySelector('[tabindex]');

    if (tabbable instanceof HTMLElement) {
      tabbable.focus();
    }
  }, []);

  const handleListKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.code) {
        case 'Tab':
        case 'Escape':
          event.preventDefault();
          onClose();

          if (chipRef.current) {
            focusChip(chipRef.current);
          }
          break;
        case 'Enter':
        case 'Space':
          event.preventDefault();
          onFilter(filters[focusedIndex]);

          if (chipRef.current) {
            focusChip(chipRef.current);
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex(Math.max(focusedIndex - 1, 0));
          break;
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex(Math.min(focusedIndex + 1, filters.length - 1));
          break;
      }
    },
    [chipRef, filters, focusedIndex, onClose, onFilter, focusChip, setFocusedIndex],
  );

  return (
    <DropdownPopover
      open={open}
      content={
        <Outside insideRefs={insideRefs} onOutside={onClose}>
          <div className={styles['popover-content']}>
            <ul
              className={styles['popover-list']}
              role="listbox"
              tabIndex={-1}
              data-testid="ChipDialogList"
              onKeyDown={handleListKeyDown}
            >
              {filters.map((item, index) => (
                <li
                  key={index}
                  className={styles['popover-list-item']}
                  role="option"
                  aria-checked={item.isActive}
                  ref={index === focusedIndex ? focusedItemRef : null}
                  onClick={() => onFilter(item)}
                  tabIndex={0}
                >
                  {item.name}
                  {item.isActive && <IconFunctionalSuccess16 color="icon-main-default" />}
                </li>
              ))}
            </ul>
          </div>
        </Outside>
      }
    >
      {anchor}
    </DropdownPopover>
  );
};
